import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import { useAuth } from '../auth/AuthContext';
import { useRooms } from './useRooms';
import { useSpotifyAccounts } from './useSpotifyAccounts';
import { useSpotifyVerifications } from './useSpotifyVerifications';
import { useSubscription } from './useSubscription';
import { PricingCards } from '../billing/PricingCards';
import { SpotifyLoginButton } from '../auth/providers/SpotifyLoginButton';
import { useAutosave } from '../../shared/hooks/useAutosave';
import { getStaticQr, upsertStaticQr, createSpotifyVerificationRequest, updateSpotifyVerificationRequestEmail, dismissSpotifyVerificationRequest } from '../../api/auth.api';
import { ROUTES, API } from '../../config/routes';
import {
  PlusIcon, GearIcon, PencilIcon, LockIcon, PrintIcon, DownloadIcon,
} from '../../shared/components/icons';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMAIL_ERRORS = {
  spotify_account_taken: 'Esta cuenta de Spotify ya está conectada a otro usuario de RockCola.',
  spotify_account_already_connected: 'Esta cuenta de Spotify ya está conectada a tu panel.',
  spotify_account_limit_reached: 'Alcanzaste el máximo de cuentas de tu plan.',
};

// Grano de película sutil sobre el fondo (mismo que RoomView).
const GRAIN = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";

const CARD = 'rounded-lg border border-lounge-line bg-lounge-card p-5 shadow-sleeve sm:p-6';
const BTN_BRASS = 'inline-flex items-center justify-center gap-2 rounded bg-lounge-brass px-5 py-3 text-sm font-semibold ' +
  'text-lounge-base transition-all hover:bg-amber hover:shadow-brass active:scale-[0.98] ' +
  'disabled:cursor-not-allowed disabled:opacity-50';
const BTN_GHOST = 'inline-flex items-center justify-center gap-2 rounded border border-lounge-line bg-lounge-raised px-4 py-2.5 ' +
  'font-mono text-[11px] uppercase tracking-[0.15em] text-lounge-text transition-colors hover:border-lounge-stone/40 ' +
  'active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50';

function Wordmark({ className = '' }) {
  return (
    <span className={`font-inter font-semibold uppercase tracking-[0.32em] text-lounge-text ${className}`}>
      Rockcola
    </span>
  );
}

function Chip({ className, children }) {
  return (
    <span className={`shrink-0 rounded-sm px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider ${className}`}>
      {children}
    </span>
  );
}

function NumberStepper({ value, onChange, min = 1, max = 99, label }) {
  const btn = 'flex h-11 w-11 items-center justify-center rounded bg-lounge-raised text-lg text-lounge-stone ' +
    'transition-colors hover:text-lounge-text disabled:cursor-not-allowed disabled:opacity-30';
  return (
    <div className="flex shrink-0 items-center gap-1 rounded-md border border-lounge-line bg-lounge-base p-1">
      <button type="button" aria-label={`Reducir ${label}`} onClick={() => onChange(Math.max(min, value - 1))}
              disabled={value <= min} className={btn}>−</button>
      <span className="w-10 text-center font-mono text-sm tabular-nums text-lounge-brass">{value}</span>
      <button type="button" aria-label={`Aumentar ${label}`} onClick={() => onChange(Math.min(max, value + 1))}
              disabled={value >= max} className={btn}>+</button>
    </div>
  );
}

function StepperRow({ label, hint, ...stepper }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-lounge-line py-4 last:border-0">
      <div className="min-w-0">
        <p className="text-[15px] font-medium text-lounge-text">{label}</p>
        <p className="mt-0.5 font-mono text-[11px] tracking-wider text-lounge-stone">{hint}</p>
      </div>
      <NumberStepper label={label.toLowerCase()} {...stepper} />
    </div>
  );
}

function Modal({ labelledBy, className = 'max-w-sm', children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm">
      <div role="dialog" aria-modal="true" aria-labelledby={labelledBy}
           className={`w-full rounded-lg border border-lounge-line bg-lounge-card p-6 shadow-sleeve ${className}`}>
        {children}
      </div>
    </div>
  );
}

export function ControlPanel() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { qrCodes, loading: generating, error: roomError, generateQRCodes } = useRooms();
  const { accounts, loading: loadingAccounts, refresh: refreshAccounts } = useSpotifyAccounts(user?.uid);
  const { requests, refresh: refreshVerifications } = useSpotifyVerifications(user?.uid);
  const { subscription, active: subActive, loading: subLoading } = useSubscription(user?.uid);
  // Sin suscripción: una sola columna centrada (cuentas arriba, planes abajo); los 3 planes no caben en la columna lateral.
  const showPlans = !subLoading && !subActive;
  const usedSpotifyAccountSlots =
    accounts.length + requests.filter((r) => r.status !== 'rejected').length;
  const atSpotifyAccountLimit = usedSpotifyAccountSlots >= (subscription?.maxSpotifyAccounts ?? 1);

  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [songLimit, setSongLimit]   = useState(2);
  const [qrCount, setQrCount]       = useState(1);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  // Las rechazadas no se listan: se avisan de a una en un modal hasta que el owner las acepta.
  const rejectedRequest = requests.find((r) => r.status === 'rejected');
  const visibleRequests = requests.filter((r) => r.status !== 'rejected');
  const [dismissing, setDismissing] = useState(false);
  const handleDismissRejected = async () => {
    setDismissing(true);
    try {
      await dismissSpotifyVerificationRequest(rejectedRequest.id);
      await refreshVerifications();
    } catch {
      // ponytail: si falla, el modal sigue abierto y el usuario puede reintentar.
    } finally {
      setDismissing(false);
    }
  };

  const [showEmailModal, setShowEmailModal] = useState(false);
  const [editingRequestId, setEditingRequestId] = useState(null);
  const [emailInput, setEmailInput]         = useState('');
  const [emailError, setEmailError]         = useState(null);
  const [submittingEmail, setSubmittingEmail] = useState(false);

  const openCreateEmailModal = () => {
    setEditingRequestId(null);
    setEmailInput('');
    setEmailError(null);
    setShowEmailModal(true);
  };

  const openEditEmailModal = (req) => {
    setEditingRequestId(req.id);
    setEmailInput(req.email);
    setEmailError(null);
    setShowEmailModal(true);
  };

  const handleSubmitEmail = async () => {
    if (!EMAIL_RE.test(emailInput)) {
      setEmailError('Ingresa un correo válido');
      return;
    }
    setSubmittingEmail(true);
    setEmailError(null);
    try {
      if (editingRequestId) {
        await updateSpotifyVerificationRequestEmail(editingRequestId, emailInput);
      } else {
        await createSpotifyVerificationRequest(emailInput);
      }
      await refreshVerifications();
      setShowEmailModal(false);
      setEditingRequestId(null);
      setEmailInput('');
    } catch (err) {
      setEmailError(
        EMAIL_ERRORS[err.response?.data?.message] ??
        (editingRequestId ? 'Error guardando el correo' : 'Error enviando la solicitud')
      );
    } finally {
      setSubmittingEmail(false);
    }
  };

  // Static QR state
  const [staticData, setStaticData]         = useState(null);   // { token, songLimit, rescanCooldownMinutes }
  const [staticSongLimit, setStaticSongLimit]   = useState(2);
  const [staticCooldown, setStaticCooldown]     = useState(10);
  const [staticDataUrl, setStaticDataUrl]       = useState(null);
  const [staticLoading, setStaticLoading]       = useState(false);
  const [staticLoadedFor, setStaticLoadedFor]   = useState(null);   // cuenta cuyos valores están en los steppers
  const [staticStatus, setStaticStatus]         = useState(null);   // 'saving' | 'saved'
  const [staticError, setStaticError]           = useState(null);

  const loadStaticQr = useCallback(async (accountId) => {
    setStaticLoading(true);
    setStaticError(null);
    setStaticStatus(null);
    setStaticData(null);
    setStaticDataUrl(null);
    try {
      const data = await getStaticQr(accountId);
      setStaticData(data);
      setStaticSongLimit(data.songLimit);
      setStaticCooldown(data.rescanCooldownMinutes);
      setStaticLoadedFor(accountId);
      if (data.token) {
        const url     = `${API.QR_SCAN_BASE_URL}?token=${data.token}`;
        const dataUrl = await QRCode.toDataURL(url, { margin: 1, scale: 6, errorCorrectionLevel: 'M' });
        setStaticDataUrl(dataUrl);
      }
    } catch (err) {
      setStaticError('Error cargando configuración');
    } finally {
      setStaticLoading(false);
    }
  }, []);

  // Auto-seleccionar la primera cuenta al cargar
  useEffect(() => {
    if (!loadingAccounts && accounts.length > 0 && !selectedAccountId) {
      setSelectedAccountId(accounts[0].id);
    }
  }, [accounts, loadingAccounts, selectedAccountId]);

  useEffect(() => {
    if (selectedAccountId) loadStaticQr(selectedAccountId);
    else { setStaticData(null); setStaticDataUrl(null); setStaticError(null); }
  }, [selectedAccountId, loadStaticQr]);

  // No toca staticData: si la respuesta llega después de cambiar de cuenta, no pisa el QR de la otra.
  const saveStaticQr = async (accountId, songLimit, rescanCooldownMinutes, hadToken) => {
    setStaticStatus('saving');
    setStaticError(null);
    try {
      const result = await upsertStaticQr(accountId, { songLimit, rescanCooldownMinutes });
      setStaticStatus('saved');
      if (result.token && !hadToken) loadStaticQr(accountId);
    } catch (err) {
      setStaticStatus(null);
      setStaticError('Error guardando configuración');
    }
  };

  useAutosave(
    () => saveStaticQr(selectedAccountId, staticSongLimit, staticCooldown, !!staticData?.token),
    [selectedAccountId, staticSongLimit, staticCooldown],
    !!selectedAccountId && staticLoadedFor === selectedAccountId && !staticLoading,
  );

  const handleGenerate = () => {
    if (!selectedAccountId) return;
    generateQRCodes(songLimit, qrCount, selectedAccountId);
  };

  const downloadStaticQr = () => {
    const img = new Image();
    img.onload = () => {
      const pad = 24;
      const textH = 48;
      const canvas = document.createElement('canvas');
      canvas.width  = img.width  + pad * 2;
      canvas.height = img.height + pad * 2 + textH;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, pad, pad);
      ctx.fillStyle = '#000000';
      ctx.font = `bold ${Math.round(img.width * 0.11)}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('rockcola.club', canvas.width / 2, img.height + pad + textH / 2);
      const a = document.createElement('a');
      a.href = canvas.toDataURL('image/png');
      a.download = 'qr-principal.png';
      a.click();
    };
    img.src = staticDataUrl;
  };

  const closeEmailModal = () => {
    setShowEmailModal(false); setEditingRequestId(null); setEmailInput(''); setEmailError(null);
  };

  return (
    <div className="relative min-h-dvh overflow-x-hidden bg-lounge-base font-inter text-lounge-text antialiased
                    selection:bg-lounge-brass/30">
      <div aria-hidden className="pointer-events-none fixed inset-0 opacity-[0.035]" style={{ backgroundImage: GRAIN }} />

      {showUpgradeModal && (
        <Modal labelledBy="upgrade-title" className="max-w-3xl">
          <div className="mb-5 flex items-center justify-between">
            <h3 id="upgrade-title" className="font-serif text-2xl font-medium text-lounge-text">Mejorar plan</h3>
            <button
              onClick={() => setShowUpgradeModal(false)}
              className="font-mono text-[11px] uppercase tracking-[0.15em] text-lounge-stone transition-colors
                         hover:text-lounge-text"
            >
              Cerrar
            </button>
          </div>
          <PricingCards planIds={subscription?.tier === 'standard_plus' ? ['vip'] : ['standard_plus', 'vip']} />
        </Modal>
      )}

      {rejectedRequest && !showEmailModal && (
        <Modal labelledBy="rejected-title">
          <h3 id="rejected-title" className="font-serif text-2xl font-medium text-lounge-text">
            Verificación rechazada
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-lounge-stone">
            No pudimos verificar la cuenta de Spotify
          </p>
          <p className="mt-3 break-all rounded border border-lounge-line bg-lounge-base px-4 py-3 font-mono text-sm
                        text-lounge-text">
            {rejectedRequest.email}
          </p>
          <p className="mt-3 text-sm leading-relaxed text-lounge-stone">
            Revisa el correo e inténtalo de nuevo con "Agregar cuenta de Spotify".
          </p>
          <div className="mt-6 flex justify-end">
            <button onClick={handleDismissRejected} disabled={dismissing} className={BTN_BRASS}>
              {dismissing ? 'Guardando…' : 'Aceptar'}
            </button>
          </div>
        </Modal>
      )}

      {showEmailModal && (
        <Modal labelledBy="email-title">
          <h3 id="email-title" className="font-serif text-2xl font-medium text-lounge-text">
            {editingRequestId ? 'Editar correo' : 'Conectar Spotify'}
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-lounge-stone">
            {editingRequestId
              ? 'Corrige el email de la cuenta de Spotify que quieres conectar.'
              : 'Ingresa el email de la cuenta de Spotify que quieres conectar. Un administrador debe autorizarla antes de poder completar la conexión.'}
          </p>
          <label htmlFor="spotify-email" className="mb-2 mt-5 block font-mono text-[11px] uppercase tracking-[0.15em] text-lounge-stone">
            Correo de Spotify
          </label>
          <input
            id="spotify-email"
            type="email"
            autoFocus
            value={emailInput}
            onChange={(e) => { setEmailInput(e.target.value); setEmailError(null); }}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSubmitEmail(); }}
            placeholder="cuenta@ejemplo.com"
            className="w-full rounded border border-lounge-line bg-lounge-base px-4 py-3 text-[16px] text-lounge-text
                       outline-none transition placeholder:text-lounge-stone focus:border-lounge-brass/60
                       focus:ring-1 focus:ring-lounge-brass/40"
          />
          {emailError && (
            <p role="alert" className="mt-3 rounded border border-stop/30 bg-stop/10 px-4 py-2.5 text-sm text-stop-soft">
              {emailError}
            </p>
          )}
          <div className="mt-6 flex justify-end gap-3">
            <button onClick={closeEmailModal} className={BTN_GHOST}>Cancelar</button>
            <button onClick={handleSubmitEmail} disabled={submittingEmail} className={BTN_BRASS}>
              {submittingEmail ? 'Guardando…' : editingRequestId ? 'Guardar' : 'Enviar'}
            </button>
          </div>
        </Modal>
      )}

      <header className="sticky top-0 z-40 border-b border-lounge-line bg-lounge-base/75
                         pt-[env(safe-area-inset-top)] backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-4 px-5 lg:px-12">
          <button type="button" onClick={() => window.location.reload()} aria-label="Recargar panel">
            <Wordmark className="text-[13px]" />
          </button>
          <div className="flex min-w-0 items-center gap-4">
            <span className="hidden truncate font-mono text-[11px] tracking-wider text-lounge-stone sm:block">
              {user?.email ?? user?.displayName}
            </span>
            <button
              onClick={logout}
              className="shrink-0 py-2 font-mono text-[11px] uppercase tracking-[0.15em] text-lounge-brass
                         transition-colors hover:text-stop-soft"
            >
              Cerrar sesión
            </button>
          </div>
        </div>
      </header>

      <main className={`relative mx-auto ${showPlans ? 'max-w-5xl' : 'max-w-6xl'} px-5 pb-16 pt-8 lg:px-12 lg:pt-12`}>
        <div className="mb-8 flex flex-wrap items-center gap-x-4 gap-y-3 lg:mb-10">
          <h1 className="font-serif text-[36px] font-semibold leading-tight tracking-tight lg:text-5xl">
            Panel de control
          </h1>
          {subActive && subscription?.expiresAt && (
            <span className="inline-flex items-center gap-2 rounded-full border border-felt/30 bg-felt/10 px-3 py-1
                             font-mono text-[11px] uppercase tracking-wider text-felt">
              <span className="h-1.5 w-1.5 rounded-full bg-felt" />
              Plan activo · vence {new Date(subscription.expiresAt).toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' })}
            </span>
          )}
        </div>

        <div className={`grid ${showPlans ? 'gap-10' : 'grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12'}`}>
          {/* Cuentas de Spotify */}
          <section aria-labelledby="accounts-title" className={showPlans ? undefined : 'lg:sticky lg:top-24 lg:col-span-5 lg:self-start'}>
            <div className="mb-4 flex items-baseline justify-between gap-3">
              <h2 id="accounts-title" className="font-serif text-[22px] font-medium">Cuentas de Spotify</h2>
              {subscription?.maxSpotifyAccounts != null && (
                <span className="font-mono text-[11px] tracking-wider text-lounge-stone">
                  {usedSpotifyAccountSlots} / {subscription.maxSpotifyAccounts} en uso
                </span>
              )}
            </div>

            <div className="flex flex-col gap-2">
              {loadingAccounts ? (
                [0, 1].map((i) => (
                  <div key={i} className="flex animate-pulse items-center justify-between rounded-md border border-lounge-line
                                          bg-lounge-card p-4 motion-reduce:animate-none">
                    <div className="flex flex-col gap-2">
                      <div className="h-3.5 w-32 rounded bg-lounge-raised" />
                      <div className="h-2.5 w-24 rounded bg-lounge-raised/60" />
                    </div>
                    <div className="h-10 w-10 rounded bg-lounge-raised" />
                  </div>
                ))
              ) : (
                accounts.map((account) => {
                  const selected = selectedAccountId === account.id;
                  return (
                    <div
                      key={account.id}
                      onClick={() => setSelectedAccountId(account.id)}
                      className={
                        'relative flex cursor-pointer items-center justify-between gap-3 overflow-hidden rounded-md border p-4 transition-colors ' +
                        (selected
                          ? 'border-lounge-brass/30 bg-lounge-raised'
                          : 'border-lounge-line bg-lounge-card hover:bg-lounge-raised/60')
                      }
                    >
                      {selected && <span aria-hidden className="absolute inset-y-0 left-0 w-[3px] bg-lounge-brass" />}
                      <button
                        type="button"
                        aria-pressed={selected}
                        className="min-w-0 flex-1 text-left"
                      >
                        <span className="block truncate text-[15px] font-medium text-lounge-text">{account.displayName}</span>
                        <span className="block truncate font-mono text-[11px] tracking-wider text-lounge-stone">
                          {account.email || account.id}
                        </span>
                      </button>
                      <button
                        type="button"
                        aria-label={`Configuración de ${account.displayName}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(ROUTES.ACCOUNT_CONFIG(account.id), { state: { email: account.email || account.id } });
                        }}
                        className="flex h-11 w-11 shrink-0 items-center justify-center rounded border border-lounge-line
                                   bg-lounge-base text-lounge-stone transition-colors hover:text-lounge-brass"
                      >
                        <GearIcon className="h-[18px] w-[18px]" />
                      </button>
                    </div>
                  );
                })
              )}

              {visibleRequests.map((req) => (
                <div key={req.id} className="flex items-center justify-between gap-3 rounded-md border border-lounge-line
                                             bg-lounge-chassis p-4">
                  <div className="min-w-0">
                    <p className="truncate font-mono text-xs tracking-wider text-lounge-text">
                      {req.email}
                    </p>
                    <div className="mt-1.5">
                      {req.status === 'pending' && <Chip className="bg-amber/10 text-amber">Pendiente</Chip>}
                      {req.status === 'verified' && <Chip className="bg-white/10 text-lounge-text">Verificada</Chip>}
                    </div>
                  </div>
                  {req.status === 'pending' && (
                    <button
                      type="button"
                      onClick={() => openEditEmailModal(req)}
                      aria-label={`Editar correo ${req.email}`}
                      className="flex h-11 w-11 shrink-0 items-center justify-center rounded border border-lounge-line
                                 bg-lounge-base text-lounge-stone transition-colors hover:text-lounge-brass"
                    >
                      <PencilIcon className="h-[18px] w-[18px]" />
                    </button>
                  )}
                  {req.status === 'verified' && (
                    <SpotifyLoginButton
                      requestId={req.id}
                      onConnected={() => { refreshAccounts(); refreshVerifications(); }}
                    />
                  )}
                </div>
              ))}

              {!loadingAccounts && accounts.length === 0 && visibleRequests.length === 0 && (
                <p className="rounded-md border border-dashed border-lounge-line px-4 py-6 text-center text-sm text-lounge-stone">
                  No hay cuentas conectadas.
                </p>
              )}
            </div>

            {atSpotifyAccountLimit ? (
              <div className="mt-4 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-t border-lounge-line pt-3">
                <p className="flex items-center gap-1.5 text-[13px] text-lounge-stone">
                  <LockIcon className="h-[15px] w-[15px] shrink-0 text-lounge-brass" />
                  Alcanzaste el máximo de cuentas de tu plan
                </p>
                {subscription?.tier !== 'vip' && (
                  <button
                    onClick={() => setShowUpgradeModal(true)}
                    className="shrink-0 font-mono text-[11px] font-medium uppercase tracking-wider text-lounge-brass hover:underline"
                  >
                    Mejorar plan →
                  </button>
                )}
              </div>
            ) : (
              <button
                onClick={openCreateEmailModal}
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-md border border-dashed
                           border-lounge-brass/40 px-4 py-3.5 font-mono text-[11px] uppercase tracking-[0.15em]
                           text-lounge-brass transition-colors hover:bg-lounge-brass/10"
              >
                <PlusIcon className="h-4 w-4" />
                Agregar cuenta de Spotify
              </button>
            )}
          </section>

          {/* Herramientas de QR */}
          <div className={`flex min-w-0 flex-col gap-8 ${showPlans ? '' : 'lg:col-span-7'}`}>
            {subLoading && (
              <div className="h-64 animate-pulse rounded-lg border border-lounge-line bg-lounge-card motion-reduce:animate-none" />
            )}

            {showPlans && (
              <section className={CARD}>
                <div className="mb-2 flex items-center gap-2">
                  <h2 className="font-serif text-[22px] font-medium">Adquiere una suscripción</h2>
                  <Chip className="bg-lounge-brass/15 text-lounge-brass">Requerida</Chip>
                </div>
                <p className="mb-6 text-sm leading-relaxed text-lounge-stone">
                  Para generar tu QR principal y los QRs temporales necesitas un plan activo.
                  Elige uno y coordina la activación por WhatsApp.
                </p>
                <PricingCards />
              </section>
            )}

            {subActive && !selectedAccountId && (
              <section className="flex flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed
                                  border-lounge-line px-6 py-14 text-center">
                <p className="font-serif text-xl italic text-lounge-text">
                  {accounts.length === 0 ? 'Conecta una cuenta de Spotify' : 'Selecciona una cuenta de Spotify'}
                </p>
                {accounts.length > 0 && (
                  <p className="text-sm text-lounge-stone">Elige una cuenta para ver y generar sus QR codes.</p>
                )}
              </section>
            )}

            {subActive && selectedAccountId && (
              <section aria-labelledby="static-title" className={CARD}>
                <div className="flex items-center gap-3">
                  <h2 id="static-title" className="font-serif text-[22px] font-medium">QR principal</h2>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-lounge-stone">
                  Un solo QR permanente para tu local. Los clientes lo escanean, consumen sus créditos y
                  pueden volver a escanear tras el tiempo de espera configurado.
                </p>

                {staticLoading ? (
                  <div className="mt-6 flex animate-pulse flex-col gap-4 motion-reduce:animate-none">
                    <div className="h-14 rounded bg-lounge-raised" />
                    <div className="h-14 rounded bg-lounge-raised" />
                    <div className="mx-auto h-[220px] w-[200px] rounded-md bg-lounge-raised" />
                  </div>
                ) : (
                  <div className="mt-4 grid gap-6 sm:grid-cols-[1fr_auto] sm:items-center lg:grid-cols-1 xl:grid-cols-[1fr_auto]">
                    <div>
                      <StepperRow label="Canciones por escaneo" hint="Rango 1–3"
                                  value={staticSongLimit} onChange={setStaticSongLimit} min={1} max={3} />
                      <StepperRow label="Tiempo de espera (minutos)" hint="Rango 1–60"
                                  value={staticCooldown} onChange={setStaticCooldown} min={1} max={60} />
                      <p role="status" className="mt-2 h-4 text-right font-mono text-[11px] tracking-wider">
                        {staticStatus === 'saving' && <span className="text-lounge-stone">Guardando…</span>}
                        {staticStatus === 'saved' && <span className="text-felt">Guardado</span>}
                      </p>
                      {staticError && (
                        <p role="alert" className="mt-3 rounded border border-stop/30 bg-stop/10 px-4 py-2.5 text-sm text-stop-soft">
                          {staticError}
                        </p>
                      )}
                    </div>

                    <div className="flex flex-col items-center gap-3">
                      {staticDataUrl ? (
                        <>
                          <div className="print-area flex flex-col items-center rounded-md bg-white p-4 shadow-sleeve">
                            <span className="mb-2 font-inter text-[10px] font-semibold uppercase tracking-[0.32em] text-black">
                              Rockcola
                            </span>
                            <a
                              href={`${API.QR_SCAN_BASE_URL}?token=${staticData.token}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="Abrir la sala en una pestaña nueva"
                              className="rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lounge-brass"
                            >
                              <img src={staticDataUrl} alt="QR principal: abrir la sala en una pestaña nueva" className="h-[180px] w-[180px]" />
                            </a>
                            <span className="mt-2 font-mono text-[10px] uppercase tracking-wider text-neutral-600">
                              Escanea para pedir música
                            </span>
                          </div>
                          <button onClick={downloadStaticQr} className={BTN_GHOST}>
                            <DownloadIcon className="h-4 w-4" />
                            Descargar
                          </button>
                        </>
                      ) : (
                        <div className="flex h-[220px] w-[200px] items-center justify-center rounded-md border border-dashed
                                        border-lounge-line px-4 text-center text-xs text-lounge-stone">
                          Ajusta la configuración para generar el QR
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </section>
            )}

            {subActive && selectedAccountId && (
              <section aria-labelledby="batch-title" className={CARD}>
                <h2 id="batch-title" className="font-serif text-[22px] font-medium">QRs de uso único</h2>
                <p className="mt-2 text-sm leading-relaxed text-lounge-stone">
                  Genera QRs temporales descartables, ideales para distribuir con un consumo específico.
                </p>

                <div className="mt-4">
                  <StepperRow label="Canciones por QR" hint="Rango 1–5"
                              value={songLimit} onChange={setSongLimit} min={1} max={5} />
                  <StepperRow label="Cantidad de QRs" hint="Rango 1–10"
                              value={qrCount} onChange={setQrCount} min={1} max={10} />
                </div>
                <button onClick={handleGenerate} disabled={generating} className={`${BTN_GHOST} mt-4 w-full py-3`}>
                  {generating ? 'Generando…' : 'Generar lote'}
                </button>

                {roomError && (
                  <p role="alert" className="mt-3 rounded border border-stop/30 bg-stop/10 px-4 py-2.5 text-sm text-stop-soft">
                    {roomError}
                  </p>
                )}

                {qrCodes.length > 0 && (
                  <div className="mt-6 border-t border-lounge-line pt-6">
                    <div className="mb-4 flex items-center justify-between gap-3">
                      <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-lounge-stone">
                        Lote generado ({qrCodes.length})
                      </span>
                      <button onClick={() => window.print()} className={BTN_GHOST}>
                        <PrintIcon className="h-4 w-4" />
                        Imprimir lote
                      </button>
                    </div>
                    <div className="print-area grid grid-cols-2 gap-3 sm:grid-cols-3">
                      {qrCodes.map((item) => (
                        <div key={item.roomId} className="flex flex-col items-center gap-2 rounded-md bg-white p-3">
                          <img src={item.dataUrl} alt={`QR ${item.roomId}`} className="aspect-square w-full max-w-[150px]" />
                          <p className="font-mono text-[11px] text-black">
                            {item.songLimit} {item.songLimit === 1 ? 'canción' : 'canciones'}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </section>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
